import { Tooltip } from '@odo/shared-ui';
import { CollapsedSideRail } from '../../side-rail/CollapsedSideRail';
import type { TimelineEvent } from '../actions/types';
import { getEventVisualConfig } from './event-config';
import { resolveActorDisplay } from './event-actor';
import { formatRelative } from './event-time';

const MAX_PREVIEW_DOTS = 5;
const TOOLTIP_MAX_WIDTH_PX = 220;

interface TimelineRailCollapsedProps {
    events: TimelineEvent[];
    total: number;
    onExpand: () => void;
}

function buildDotTooltip(event: TimelineEvent) {
    const title = getEventVisualConfig(event.type).formatTitle(event);
    const actor = resolveActorDisplay(event);
    const relative = formatRelative(event.createdAt);
    const meta = [actor.name, relative].filter(Boolean).join(' · ');
    return (
        <div
            className="flex flex-col gap-0.5 font-inter"
            style={{ maxWidth: TOOLTIP_MAX_WIDTH_PX }}
        >
            <span className="text-[12px] font-semibold text-gray-900 leading-tight">{title}</span>
            {meta && (
                <span className="text-[11px] text-gray-500 leading-tight capitalize">{meta}</span>
            )}
        </div>
    );
}

function TimelineRailCollapsed({ events, total, onExpand }: TimelineRailCollapsedProps) {
    const previewEvents = events.slice(0, MAX_PREVIEW_DOTS);
    const labelText = total > 0 ? `Timeline · ${total}` : 'Timeline';

    const previewDots =
        previewEvents.length > 0
            ? previewEvents.map((event) => {
                  const tone = getEventVisualConfig(event.type).tone;
                  return (
                      <Tooltip key={event.id} side="left" content={buildDotTooltip(event)}>
                          <span
                              className={`relative w-3.5 h-3.5 rounded-full border flex items-center justify-center ${tone.ring}`}
                          >
                              <span className={`w-1 h-1 rounded-full ${tone.dot}`} />
                          </span>
                      </Tooltip>
                  );
              })
            : undefined;

    return (
        <CollapsedSideRail
            side="right"
            panelName="timeline"
            label={labelText}
            onExpand={onExpand}
            trailing={previewDots}
        />
    );
}

export { TimelineRailCollapsed };
